import { defineStore, type _GettersTree } from 'pinia';
import type {
    ValidationErrorStateType,
    ValidationErrorGettersType,
    ValidationErrorActionsType,
    ValidationErrorType,
    InputRegistrationDataType,
    InputValidationReturnType,
} from '@/types/stores/ValidationErrorStoreTypes';

export const useValidationErrorStore = defineStore<
    'validationError',
    ValidationErrorStateType,
    ValidationErrorGettersType & _GettersTree<ValidationErrorStateType>,
    ValidationErrorActionsType
>('validationError', {
    state: (): ValidationErrorStateType => ({
        isLoading: false,
        errors: new Map<string, ValidationErrorType>(),
        currentFormId: null,
        currentPanelIndex: null,
        currentTabId: null,
    }),
    getters: {
        getErrorsForForm: (state: ValidationErrorStateType) => (formId: string) => {
            const formErrors: ValidationErrorType[] = [];
            for (const error of state.errors.values()) {
                if (error.formId === formId) {
                    formErrors.push(error);
                }
            }

            return formErrors;
        },
        getTabErrors: (state: ValidationErrorStateType) => (formId: string, panelIndex?: number) => {
            const tabErrors: Record<string, boolean> = {};

            for (const error of state.errors.values()) {
                if (error.formId === formId && !error.isValid && error.errorMessage) {
                    if (panelIndex !== undefined && error.panelIndex !== panelIndex) {
                        continue;
                    }
                    if (error.tabId) {
                        tabErrors[error.tabId] = true;
                    }
                }
            }

            return tabErrors;
        },
        getPanelErrors: (state: ValidationErrorStateType) => (formId: string) => {
            const panelErrors: Record<number, boolean> = {};

            for (const error of state.errors.values()) {
                if (error.formId === formId && !error.isValid && error.errorMessage) {
                    if (error.panelIndex !== null) {
                        panelErrors[error.panelIndex] = true;
                    }
                }
            }

            return panelErrors;
        },
        hasErrorsInTab: (state: ValidationErrorStateType) => (formId: string, tabId: string, panelIndex?: number) => {
            for (const error of state.errors.values()) {
                if (
                    error.formId === formId &&
                    error.tabId === tabId &&
                    !error.isValid &&
                    error.errorMessage &&
                    (panelIndex === undefined || error.panelIndex === panelIndex)
                ) {
                    return true;
                }
            }

            return false;
        },
        hasErrorsInPanel: (state: ValidationErrorStateType) => (formId: string, panelIndex: number) => {
            for (const error of state.errors.values()) {
                if (
                    error.formId === formId &&
                    error.panelIndex === panelIndex &&
                    !error.isValid &&
                    error.errorMessage
                ) {
                    return true;
                }
            }

            return false;
        },
    },
    actions: {
        setCurrentForm(formId: string): void {
            this.currentFormId = formId;
        },
        setCurrentPanel(panelIndex: number | null): void {
            this.currentPanelIndex = panelIndex;
        },
        setCurrentTab(tabId: string | null): void {
            this.currentTabId = tabId;
        },
        generateInputId(inputData: InputRegistrationDataType): string {
            const parts = [inputData.formId];

            if (inputData.panelIndex !== null) {
                parts.push(inputData.panelIndex.toString());
            }

            if (inputData.tabId) {
                parts.push(inputData.tabId);
            }

            parts.push(inputData.fieldName);

            return parts.join('-');
        },
        registerInput(fieldName: string, panelIndex: number | null, tabId: string | null): InputValidationReturnType {
            const inputData: InputRegistrationDataType = {
                formId: this.currentFormId || 'unknown',
                fieldName,
                panelIndex: panelIndex ?? this.currentPanelIndex,
                tabId: tabId ?? this.currentTabId,
            };

            const inputId = this.generateInputId(inputData);

            if (!this.errors.has(inputId)) {
                this.errors.set(inputId, {
                    inputId,
                    formId: inputData.formId,
                    panelIndex: inputData.panelIndex,
                    tabId: inputData.tabId,
                    fieldName: inputData.fieldName,
                    errorMessage: '',
                    isValid: true,
                });
            }

            return {
                inputId,
                setError: (errorMessage: string) => {
                    this.setInputError(inputId, errorMessage, false);
                },
                clearError: () => {
                    this.clearInputError(inputId);
                },
                hasError: () => {
                    const error = this.errors.get(inputId);

                    return error ? !error.isValid && !!error.errorMessage : false;
                },
                errorMessage: () => {
                    const error = this.errors.get(inputId);

                    return error && !error.isValid ? error.errorMessage : undefined;
                },
            };
        },
        unregisterInput(inputId: string): void {
            this.errors.delete(inputId);
        },
        setInputError(inputId: string, errorMessage: string, isValid = false): void {
            const error = this.errors.get(inputId);
            if (error) {
                error.errorMessage = errorMessage;
                error.isValid = isValid;
            }
        },
        clearInputError(inputId: string): void {
            const error = this.errors.get(inputId);
            if (error) {
                error.errorMessage = '';
                error.isValid = true;
            }
        },
        clearFormErrors(formId: string): void {
            for (const [_key, error] of this.errors) {
                if (error.formId === formId) {
                    error.errorMessage = '';
                    error.isValid = true;
                }
                break;
            }
        },
    },
});
